import { useState, useEffect, useRef } from 'react';
import { Plus, ChevronDown, ChevronRight, Pencil, Trash2, FolderPlus, X, Settings2, Search, AlertCircle, Palette, RefreshCw, Type, Tag, TrendingUp, TrendingDown, Smile, FileText, RotateCcw, Check } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuthStore, useCategories, useDefaultCategories } from '@flows.cash/core';
import { MainCategory, SubCategory } from "@flows.cash/types";
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { motion } from 'framer-motion';
import { HexColorPicker } from 'react-colorful';
import { cn } from '@/lib/utils';
import { v4 as uuidv4 } from 'uuid';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Updated icon categories with only valid Lucide icons
const iconCategories = {
  'Finance & Money': [
    'Wallet', 'CreditCard', 'PiggyBank', 'Banknote', 'Receipt', 'DollarSign', 'CircleDollarSign',
    'Landmark', 'BarChart3', 'Percent', 'TrendingUp', 'TrendingDown', 'Calculator'
  ],
  'Home & Living': [
    'Home', 'Building2', 'DoorClosed', 'Sofa', 'Bed', 'Lightbulb', 'Lamp',
    'TreePine', 'Armchair', 'Bath', 'BedDouble'
  ],
  'Transportation': [
    'Car', 'Bus', 'Plane', 'Train', 'Bike', 'Truck', 'Ship', 'Navigation', 'MapPin', 'Map',
    'Fuel', 'ParkingCircle', 'Taxi'
  ],
  'Food & Dining': [
    'Utensils', 'Coffee', 'Wine', 'Apple', 'Beer', 'Cookie', 'Pizza', 'UtensilsCrossed',
    'ChefHat', 'IceCream'
  ],
  'Shopping & Services': [
    'ShoppingBag', 'ShoppingCart', 'Store', 'Package', 'Gift', 'Scissors', 'Briefcase', 'Tags',
    'Shirt', 'ShoppingBasket'
  ],
  'Health & Wellness': [
    'Heart', 'Stethoscope', 'Activity', 'Pill', 'Dumbbell', 'Brain', 'Eye', 'Smile',
    'Thermometer', 'Cross'
  ],
  'Entertainment': [
    'Gamepad', 'Gamepad2', 'Music', 'Film', 'Tv', 'Radio', 'Ticket', 'Clapperboard', 'Play',
    'Headphones', 'Mic'
  ],
  'Technology': [
    'Smartphone', 'Laptop', 'Monitor', 'Wifi', 'Printer', 'Mouse', 'Keyboard', 'Camera',
    'HardDrive', 'Cpu'
  ],
  'Utilities': [
    'Zap', 'Flame', 'Droplets', 'Droplet', 'Phone', 'Signal', 'Power', 'Battery'
  ],
  'Personal': [
    'User', 'Users', 'Heart', 'Star', 'Smile', 'UserPlus', 'Glasses', 'Brush', 'Sparkles',
    'Crown', 'Gift'
  ],
  'Business': [
    'BarChart3', 'TrendingUp', 'LineChart', 'PieChart', 'Target', 'Award', 'CheckCircle',
    'Briefcase', 'Building'
  ],
  'Pets': [
    'Cat', 'Dog', 'Fish', 'Bird', 'Rabbit', 'Bone', 'Paw'
  ],
  'Family': [
    'Baby', 'Users', 'Heart', 'Milk', 'Puzzle', 'GraduationCap'
  ],
  'Travel': [
    'Plane', 'Hotel', 'Bed', 'PalmTree', 'Map', 'MapPin', 'Compass', 'Luggage'
  ],
  'Other': [
    'Folder', 'Files', 'FileText', 'Settings', 'Bell', 'Calendar', 'Clock', 'Flag', 'Building',
    'Circle', 'Plus', 'MoreHorizontal', 'RefreshCw', 'Repeat', 'HandHeart', 'Wrench'
  ]
};

function IconPicker({ value, onChange }: { value: string; onChange: (icon: string) => void }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const IconComponent = (LucideIcons as any)[value] || LucideIcons.Folder;

  // Filter icons based on search query
  const filteredIcons = searchQuery
    ? Object.values(iconCategories).flat().filter(name => 
        name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Get category for current icon
  const getCurrentIconCategory = () => {
    for (const [category, icons] of Object.entries(iconCategories)) {
      if (icons.includes(value)) {
        return category;
      }
    }
    return 'Other';
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark rounded-xl h-12 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <IconComponent className="h-5 w-5 text-primary" />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-text-primary dark:text-text-dark-primary font-medium">{value}</span>
              <span className="text-xs text-text-secondary">{getCurrentIconCategory()}</span>
            </div>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-text-secondary" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[450px] max-h-[80vh] p-0 bg-background-secondary/95 dark:bg-background-dark-secondary/95 backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex flex-col h-full">
          <DialogHeader className="flex-shrink-0 p-6 border-b border-border/30 dark:border-border-dark/30 bg-gradient-to-r from-primary/5 to-secondary/5">
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Choose Icon
            </DialogTitle>
            <div className="flex items-center gap-2 mt-3">
              <Search className="w-4 h-4 text-text-secondary" />
              <Input
                placeholder="Search icons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            {searchQuery && (
              <div className="text-xs text-text-secondary mt-2">
                Found {filteredIcons.length} icon{filteredIcons.length !== 1 ? 's' : ''}
              </div>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(80vh - 140px)' }}>
            {searchQuery ? (
              <div className="grid grid-cols-5 gap-3">
                {filteredIcons.map((name) => {
                  const Icon = (LucideIcons as any)[name];
                  // Skip if icon doesn't exist in Lucide
                  if (!Icon) return null;
                  
                  return (
                    <Button
                      key={name}
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-20 w-20 rounded-xl flex flex-col items-center justify-center gap-1 hover:bg-primary/10 dark:hover:bg-primary/10 transition-all duration-200 border-2 border-transparent",
                        value === name && "bg-primary/15 text-primary ring-2 ring-primary/30 border-primary/30"
                      )}
                      onClick={() => {
                        onChange(name);
                        setIsOpen(false);
                      }}
                      title={name}
                    >
                      <Icon className="h-8 w-8" />
                      <span className="text-[10px] opacity-60 truncate max-w-[90%] text-center font-medium">
                        {name}
                      </span>
                    </Button>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(iconCategories).map(([category, icons]) => (
                  <div key={category} className="space-y-3">
                    <div 
                      className="px-4 py-3 text-sm font-semibold text-text-primary dark:text-text-dark-primary flex items-center justify-between cursor-pointer hover:bg-primary/10 dark:hover:bg-primary/10 rounded-lg transition-colors duration-200 bg-background/50 dark:bg-background-dark/50"
                      onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                    >
                      <span>{category}</span>
                      <ChevronDown 
                        className={cn(
                          "w-4 h-4 transition-transform",
                          selectedCategory === category ? "transform rotate-180" : ""
                        )}
                      />
                    </div>
                    {selectedCategory === category && (
                      <div className="grid grid-cols-4 gap-3 py-2 px-2 border-l-2 border-primary/30 dark:border-primary-dark/30 ml-2 bg-background/30 dark:bg-background-dark/30 rounded-r-lg">
                        {icons.map((name) => {
                          const Icon = (LucideIcons as any)[name];
                          // Skip if icon doesn't exist in Lucide
                          if (!Icon) return null;
                          
                          return (
                            <Button
                              key={name}
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-16 w-16 rounded-xl flex flex-col items-center justify-center gap-1 hover:bg-primary/10 dark:hover:bg-primary/10 transition-all duration-200 border-2 border-transparent",
                                value === name && "bg-primary/15 text-primary ring-2 ring-primary/30 border-primary/30"
                              )}
                              onClick={() => {
                                onChange(name);
                                setIsOpen(false);
                              }}
                              title={name}
                            >
                              <Icon className="h-6 w-6" />
                              <span className="text-[9px] opacity-60 truncate max-w-[90%] text-center">
                                {name}
                              </span>
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryModalProps {
  onClose: () => void;
  onSave: (data: any) => void;
  type: 'main' | 'sub';
  parentCategory?: MainCategory;
  editingCategory?: MainCategory | SubCategory;
  mode: 'add' | 'edit';
}

function CategoryModal({ onClose, onSave, type, parentCategory, editingCategory, mode }: CategoryModalProps) {
  const { t } = useTranslation();
  // For subcategories, always use parent category's color and type
  const initialColor = type === 'sub' 
    ? (parentCategory?.color || '#439476')
    : (editingCategory?.color || '#439476');

  const [formData, setFormData] = useState({
    name: editingCategory?.name || '',
    type: type === 'sub' 
      ? (parentCategory?.type || 'expense')
      : ((editingCategory as MainCategory)?.type || 'expense'),
    color: initialColor,
    icon: (editingCategory as MainCategory)?.icon || (editingCategory as SubCategory)?.icon || 'Folder',
    description: editingCategory?.description || '',
    trackInNetWorth: editingCategory?.trackInNetWorth ?? true,
    isRecurring: editingCategory?.isRecurring ?? false,
    transactionCount: (editingCategory as SubCategory)?.transactionCount || 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (type === 'main') {
      const newCategory = {
        id: editingCategory?.id || uuidv4(),
        name: formData.name,
        type: formData.type as 'income' | 'expense',
        color: formData.color,
        icon: formData.icon,
        description: formData.description,
        trackInNetWorth: formData.trackInNetWorth,
        isRecurring: formData.isRecurring,
        subcategories: (editingCategory as MainCategory)?.subcategories || [],
        isExpanded: (editingCategory as MainCategory)?.isExpanded || false,
        transactionCount: formData.transactionCount
      };
      onSave(newCategory);
    } else if (type === 'sub' && parentCategory) {
      const newSubcategory = {
        id: editingCategory?.id || uuidv4(),
        name: formData.name,
        type: parentCategory.type,
        color: parentCategory.color,
        icon: formData.icon,
        description: formData.description,
        trackInNetWorth: formData.trackInNetWorth,
        isRecurring: formData.isRecurring,
        parentId: parentCategory.id,
        transactionCount: formData.transactionCount
      };
      onSave(newSubcategory);
    }
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-1rem)] sm:max-w-[600px] lg:max-w-[700px] bg-gradient-to-br from-background-secondary/95 to-background-secondary/85 dark:from-background-dark-secondary/95 dark:to-background-dark-secondary/85 backdrop-blur-2xl backdrop-saturate-150 border border-border/50 dark:border-border-dark/50 shadow-2xl shadow-black/10 dark:shadow-black/40 p-0 rounded-2xl overflow-hidden">
        {/* Header with gradient background */}
        <div className="relative bg-gradient-to-r from-primary/10 via-primary/5 to-secondary/10 dark:from-primary-dark/10 dark:via-primary-dark/5 dark:to-secondary-dark/10 px-6 py-5 border-b border-border/30 dark:border-border-dark/30">
          <div className="flex items-center gap-4">
            {/* Icon preview */}
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
              style={{ 
                backgroundColor: formData.color,
                boxShadow: `0 4px 12px ${formData.color}25`
              }}
            >
              <CategoryIcon 
                iconName={formData.icon} 
                className="w-6 h-6 text-white drop-shadow-sm" 
              />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl font-bold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent mb-1">
                {mode === 'add' ? t(`settings.categoriesSettings.${type === 'main' ? 'addCategory' : 'addSubcategory'}`) : 
                 t(`settings.categoriesSettings.${type === 'main' ? 'editCategory' : 'editSubcategory'}`)}
              </DialogTitle>
              <DialogDescription className="text-text-secondary text-sm">
                {type === 'main'
                  ? t('settings.categoriesSettings.description')
                  : t('settings.categoriesSettings.form.parentCategoryDesc', { name: parentCategory?.name })}
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Form content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-5 bg-gradient-to-b from-primary to-secondary rounded-full"></div>
              <h3 className="font-semibold text-text-primary dark:text-text-dark-primary">Basic Information</h3>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Name field */}
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  <Type className="w-4 h-4" />
                  Category Name
                </Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={type === 'main' ? "e.g., Groceries, Entertainment, Salary" : "e.g., Restaurants, Movies, Freelance"}
                  className="bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark rounded-xl h-12 text-base transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  required
                />
              </div>

              {/* Type selector for main categories */}
              {type === 'main' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Category Type
                  </Label>
                  <div className="grid grid-cols-2 gap-2 p-1 bg-background-secondary/50 dark:bg-background-dark-secondary/50 rounded-xl border border-border dark:border-border-dark">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, type: 'income' })}
                      className={cn(
                        "flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                        formData.type === 'income' 
                          ? "bg-green-500 text-white shadow-lg shadow-green-500/20" 
                          : "text-text-secondary hover:text-text-primary hover:bg-background-secondary/80"
                      )}
                    >
                      <TrendingUp className="w-4 h-4" />
                      Income
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, type: 'expense' })}
                      className={cn(
                        "flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                        formData.type === 'expense' 
                          ? "bg-red-500 text-white shadow-lg shadow-red-500/20" 
                          : "text-text-secondary hover:text-text-primary hover:bg-background-secondary/80"
                      )}
                    >
                      <TrendingDown className="w-4 h-4" />
                      Expense
                    </button>
                  </div>
                </div>
              )}

              {/* Icon picker */}
              <div className={cn("space-y-2", type === 'main' ? "" : "md:col-span-2")}>
                <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  <Smile className="w-4 h-4" />
                  Icon
                </Label>
                <div className="relative">
                  <IconPicker
                    value={formData.icon}
                    onChange={(icon) => setFormData({ ...formData, icon })}
                  />
                </div>
              </div>

              {/* Color picker for main categories */}
              {type === 'main' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Color
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal h-12",
                          "bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark rounded-xl",
                          "hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="h-8 w-8 rounded-lg border-2 border-white shadow-sm"
                            style={{ backgroundColor: formData.color }}
                          />
                          <div className="flex-1">
                            <span className="text-text-primary dark:text-text-dark-primary font-medium">
                              {formData.color}
                            </span>
                          </div>
                          <ChevronDown className="h-4 w-4 text-text-secondary" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-auto p-4 bg-background-secondary/95 dark:bg-background-dark-secondary/95 border border-border/50 dark:border-border-dark/50 backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-2xl"
                      align="start"
                    >
                      <div className="space-y-4">
                        <HexColorPicker
                          color={formData.color}
                          onChange={(color) => setFormData({ ...formData, color })}
                        />
                        <div className="flex items-center gap-2">
                          <Input
                            value={formData.color}
                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                            className="h-9 bg-background-secondary dark:bg-background-dark-secondary border-border dark:border-border-dark rounded-lg"
                            placeholder="#000000"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-9 border-border dark:border-border-dark hover:bg-primary/5 dark:hover:bg-primary/10 rounded-lg"
                            onClick={() => setFormData({ ...formData, color: '#439476' })}
                          >
                            Reset
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Description
                <span className="text-xs text-text-secondary font-normal">(Optional)</span>
              </Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add a description to help identify this category's purpose..."
                rows={3}
                className="w-full bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark rounded-xl p-4 text-text-primary dark:text-text-dark-primary placeholder-text-secondary resize-none transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>

          {/* Settings Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-5 bg-gradient-to-b from-secondary to-primary rounded-full"></div>
              <h3 className="font-semibold text-text-primary dark:text-text-dark-primary">Category Settings</h3>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Track in Net Worth */}
              <div className="group p-4 rounded-xl bg-gradient-to-br from-background-secondary/80 to-background-secondary/60 dark:from-background-dark-secondary/80 dark:to-background-dark-secondary/60 border border-border/50 dark:border-border-dark/50 hover:shadow-lg transition-all duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        Track in Net Worth
                      </Label>
                    </div>
                    <p className="text-xs text-text-secondary leading-relaxed">
                      Include this category in net worth calculations and financial reports
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, trackInNetWorth: !formData.trackInNetWorth })}
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 focus:ring-offset-background-secondary",
                      formData.trackInNetWorth 
                        ? "bg-gradient-to-r from-primary to-secondary shadow-lg" 
                        : "bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark"
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300",
                        formData.trackInNetWorth ? "translate-x-6" : "translate-x-1"
                      )}
                    />
                  </button>
                </div>
              </div>

              {/* Recurring Category */}
              <div className="group p-4 rounded-xl bg-gradient-to-br from-background-secondary/80 to-background-secondary/60 dark:from-background-dark-secondary/80 dark:to-background-dark-secondary/60 border border-border/50 dark:border-border-dark/50 hover:shadow-lg transition-all duration-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <RotateCcw className="w-4 h-4 text-secondary" />
                      <Label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        Recurring Category
                      </Label>
                    </div>
                    <p className="text-xs text-text-secondary leading-relaxed">
                      Mark as a recurring expense or income category for budgeting
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, isRecurring: !formData.isRecurring })}
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:ring-offset-2 focus:ring-offset-background-secondary",
                      formData.isRecurring 
                        ? "bg-gradient-to-r from-secondary to-primary shadow-lg" 
                        : "bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark"
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300",
                        formData.isRecurring ? "translate-x-6" : "translate-x-1"
                      )}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Parent Category Info for subcategories */}
          {type === 'sub' && parentCategory && (
            <div className="p-4 rounded-xl bg-gradient-to-r from-background-secondary/50 to-background-secondary/30 dark:from-background-dark-secondary/50 dark:to-background-dark-secondary/30 border border-border/30 dark:border-border-dark/30">
              <div className="flex items-center gap-4">
                <div 
                  className="p-3 rounded-xl shadow-lg"
                  style={{ backgroundColor: parentCategory.color + '20' }}
                >
                  <div
                    className="w-5 h-5 rounded-full shadow-sm"
                    style={{ backgroundColor: parentCategory.color }}
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary mb-1">
                    Parent Category: {parentCategory.name}
                  </h4>
                  <p className="text-xs text-text-secondary">
                    This subcategory will inherit the color and type from its parent category
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t border-border/30 dark:border-border-dark/30">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="w-full sm:w-auto h-12 px-6 text-text-secondary hover:text-text-primary hover:bg-background-secondary/50 dark:hover:bg-background-dark-secondary/50 rounded-xl transition-all duration-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!formData.name.trim()}
              className={cn(
                "w-full sm:w-auto h-12 px-8 font-medium rounded-xl transition-all duration-200 shadow-lg",
                "bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90",
                "text-white shadow-primary/20 hover:shadow-xl hover:shadow-primary/30",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none",
                "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 focus:ring-offset-background-secondary"
              )}
            >
              <div className="flex items-center gap-2">
                {mode === 'add' ? <Plus className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                {mode === 'add' ? 'Create Category' : 'Save Changes'}
              </div>
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Update the CategoryIcon component
const CategoryIcon = ({ iconName, className, style }: { iconName: string; className?: string; style?: React.CSSProperties }) => {
  // Get the icon component from LucideIcons
  const Icon = (LucideIcons as any)[iconName];
  
  // If the icon doesn't exist, try to find a suitable replacement
  if (!Icon) {
    // Map of fallback icons for common missing icons
    const fallbackMap: Record<string, string> = {
      'Paw': 'Cat',
      'Pet': 'Cat',
      'Animal': 'Cat',
      'Dog': 'Cat',
      'Fish': 'Cat',
      'Bird': 'Cat'
    };
    
    // Try to get a suitable replacement
    const fallbackIconName = fallbackMap[iconName] || 'Folder';
    const FallbackIcon = (LucideIcons as any)[fallbackIconName];
    
    console.warn(`Icon ${iconName} not found, using ${fallbackIconName} as fallback`);
    return <FallbackIcon className={className} style={style} />;
  }
  
  return <Icon className={className} style={style} />;
};

export default function CategoriesSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { 
    categories, 
    isLoading, 
    // We're not using saveCategories anymore as it causes SQL issues with large DELETE queries
    // saveCategories, 
    addCategory, 
    addSubcategory, 
    updateCategory, 
    deleteCategory,
    refetch
  } = useCategories();
  const defaultCategories = useDefaultCategories();
  const [showMainCategoryModal, setShowMainCategoryModal] = useState(false);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MainCategory | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all');
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Only initialize expanded state if not already initialized and categories are loaded
    if (!initializedRef.current && categories.length > 0) {
      const initialExpanded = categories.reduce((acc: Record<string, boolean>, category: MainCategory) => ({
        ...acc,
        [category.id]: category.isExpanded ?? false
      }), {});
      setExpandedCategories(initialExpanded);
      initializedRef.current = true;
    }
  }, [categories]);

  const filteredCategories = categories.filter((category: MainCategory) => {
    const matchesSearch = category.name.toLowerCase().includes(filter.toLowerCase()) ||
      (category.subcategories || []).some((sub: any) => sub.name.toLowerCase().includes(filter.toLowerCase()));
    const matchesType = typeFilter === 'all' || category.type === typeFilter;
    return matchesSearch && matchesType;
  }).sort((a, b) => a.name.localeCompare(b.name));

  const handleEditMainCategory = (category: MainCategory) => {
    setSelectedCategory(category);
    setModalMode('edit');
    setShowMainCategoryModal(true);
  };

  const handleEditSubCategory = (category: MainCategory, subcategory: SubCategory) => {
    setSelectedCategory(category);
    setSelectedSubCategory(subcategory);
    setModalMode('edit');
    setShowSubCategoryModal(true);
  };

  const handleAddMainCategory = async (newCategory: MainCategory) => {
    if (!user?.id) return;

    try {
      // Check if a category with the same name already exists
      const existingCategory = categories.find(
        cat => cat.name.toLowerCase() === newCategory.name.toLowerCase()
      );
      
      if (existingCategory) {
        toast.error('A category with this name already exists');
        return;
      }
      
      // Add the new category
      await addCategory(newCategory);
      toast.success('Category added successfully');
      setShowMainCategoryModal(false);
    } catch (error: any) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category');
    }
  };

  const handleUpdateMainCategory = async (categoryId: string, updatedCategory: Partial<MainCategory>) => {
    if (!user?.id) return;

    try {
      // Check if the category exists
      const category = categories.find(cat => cat.id === categoryId);
      if (!category) {
        toast.error('Category not found');
        return;
      }
      
      // Update the category
      await updateCategory({
        ...category,
        ...updatedCategory,
        // We need to exclude subcategories from the update
        // @ts-ignore - we're intentionally removing subcategories
        subcategories: undefined
      });

      toast.success('Category updated successfully');
      setShowMainCategoryModal(false);
    } catch (error: any) {
      console.error('Error updating category:', error);
      
      // Provide a more user-friendly error message
      if (error.code === '23503' && error.message?.includes('foreign key constraint')) {
        toast.error('Cannot update a category that is used in transactions. Please reassign those transactions first.');
      } else {
        toast.error('Failed to update category');
      }
    }
  };

  const handleDeleteMainCategory = async (categoryId: string) => {
    if (!user?.id) return;

    // Find the category to get its name and subcategories count
    const categoryToDelete = categories.find(cat => cat.id === categoryId);
    if (!categoryToDelete) return;

    const subcategoriesCount = (categoryToDelete.subcategories as SubCategory[])?.length || 0;
    
    // Create a more informative confirmation message
    const confirmMessage = subcategoriesCount > 0 
      ? t('settings.categoriesSettings.deleteConfirm.description', { name: categoryToDelete.name }) + 
        ` This will also delete ${subcategoriesCount} subcategory${subcategoriesCount !== 1 ? 'ies' : ''} under this category.`
      : t('settings.categoriesSettings.deleteConfirm.description', { name: categoryToDelete.name });

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      // Delete the category
      await deleteCategory(categoryId);
      toast.success(t('settings.categoriesSettings.messages.deleteSuccess'));
    } catch (error: any) {
      console.error('Error deleting category:', error);
      
      // Check for specific error messages
      if (error.message && error.message.includes('used in transactions')) {
        toast.error(t('settings.categoriesSettings.usedInTransactionsError'), {
          duration: 5000,
          style: {
            border: '1px solid #ef4444',
            padding: '16px',
            color: '#ef4444',
          },
        });
      } else {
        // For other errors, use the message from the hook
        toast.error(error.message || t('settings.categoriesSettings.messages.error'));
      }
    }
  };

  const handleUpdateSubcategory = async (
    mainCategoryId: string,
    subcategoryId: string,
    updatedSubcategory: Partial<SubCategory>
  ) => {
    if (!user?.id) return;

    try {
      // Find the main category
      const mainCategory = categories.find(cat => cat.id === mainCategoryId);
      if (!mainCategory) {
        toast.error('Category not found');
        return;
      }
      
      // Check if the subcategory exists
      const subcategory = (mainCategory.subcategories as SubCategory[]).find((sub: SubCategory) => sub.id === subcategoryId);
      if (!subcategory) {
        toast.error('Subcategory not found');
        return;
      }
      
      // Create updated subcategory with the required properties preserved
      const subcategoryToUpdate: any = {
        ...subcategory,
        // Only update these properties from the form
        name: updatedSubcategory.name || subcategory.name,
        icon: updatedSubcategory.icon || subcategory.icon,
        // Critical: Preserve these properties to ensure it remains a subcategory
        parentId: mainCategoryId,
        // IMPORTANT: Add parent_id in snake_case format for the database service
        parent_id: mainCategoryId,
        type: mainCategory.type as 'income' | 'expense',
        color: mainCategory.color,
        // Ensure transactionCount is a number
        transactionCount: subcategory.transactionCount || 0,
        id: subcategoryId
      };
      
      // Update just the specific subcategory using updateCategory
      // This avoids the large DELETE query that's causing the error
      await updateCategory(subcategoryToUpdate);
      
      // Refetch categories to update the local state
      await refetch();
      
      toast.success('Subcategory updated successfully');
      setShowSubCategoryModal(false);
    } catch (error: any) {
      console.error('Error updating subcategory:', error);
      
      // Provide a more user-friendly error message
      if (error.code === '23503' && error.message?.includes('foreign key constraint')) {
        toast.error('Cannot update a subcategory that is used in transactions. Please reassign those transactions first.');
      } else {
        toast.error('Failed to update subcategory');
      }
    }
  };

  const handleDeleteSubcategory = async (mainCategoryId: string, subcategoryId: string) => {
    if (!user?.id) return;

    // Find the main category and subcategory to get names
    const mainCategory = categories.find(cat => cat.id === mainCategoryId);
    if (!mainCategory) return;
    
    const subcategory = (mainCategory.subcategories as SubCategory[]).find((sub: SubCategory) => sub.id === subcategoryId);
    if (!subcategory) return;

    const confirmMessage = t('settings.categoriesSettings.deleteConfirm.description', { name: subcategory.name });
    
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      // Delete the subcategory
      await deleteCategory(subcategoryId);
      toast.success(t('settings.categoriesSettings.messages.deleteSuccess'));
    } catch (error: any) {
      console.error('Error deleting subcategory:', error);
      
      // Check for specific error messages
      if (error.message && error.message.includes('used in transactions')) {
        toast.error(t('settings.categoriesSettings.usedInTransactionsError'), {
          duration: 5000,
          style: {
            border: '1px solid #ef4444',
            padding: '16px',
            color: '#ef4444',
          },
        });
      } else {
        // For other errors, use the message from the hook
        toast.error(error.message || t('settings.categoriesSettings.messages.error'));
      }
    }
  };

  const handleAddSubcategoryClick = (category: MainCategory) => {
    setSelectedCategory(category);
    setSelectedSubCategory(null);
    setModalMode('add');
    setShowSubCategoryModal(true);
  };

  const handleAddSubCategory = async (newSubcategory: SubCategory) => {
    if (!user?.id || !selectedCategory) return;

    try {
      // Check if a subcategory with the same name already exists in this category
      const existingSubcategory = (selectedCategory.subcategories as SubCategory[]).find(
        (sub: SubCategory) => sub.name.toLowerCase() === newSubcategory.name.toLowerCase()
      );
      
      if (existingSubcategory) {
        toast.error('A subcategory with this name already exists in this category');
        return;
      }
      
      // Add the new subcategory
      await addSubcategory({
        parentId: selectedCategory.id,
        subcategory: newSubcategory
      });
      
      toast.success('Subcategory added successfully');
      setShowSubCategoryModal(false);
      setSelectedCategory(null);
      setSelectedSubCategory(null);
    } catch (error: any) {
      console.error('Error adding subcategory:', error);
      toast.error('Failed to add subcategory');
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleResetToDefaultCategories = async () => {
    try {
      // Create a map of existing categories by ID for quick lookup to avoid duplicates
      const existingCategoryIdsMap = new Map<string, MainCategory>();
      // Create a map of existing categories by name for quick lookup
      const existingCategoryNamesMap = new Map<string, MainCategory>();
      
      categories.forEach((category: MainCategory) => {
        existingCategoryIdsMap.set(category.id, category);
        existingCategoryNamesMap.set(category.name.toLowerCase(), category);
      });

      console.log('Default categories:', defaultCategories.map(c => c.name));
      console.log('Existing categories:', categories.map(c => c.name));
      console.log('Missing categories:', 
        defaultCategories.filter(d => !categories.some(c => c.name.toLowerCase() === d.name.toLowerCase())).map(c => c.name)
      );
      
      // Deep check for missing subcategories
      const missingSubcatsCheck = [];
      for (const defaultCat of defaultCategories) {
        const matchingCat = categories.find(c => c.name.toLowerCase() === defaultCat.name.toLowerCase());
        if (matchingCat) {
          // Check if all default subcategories exist in this category
          for (const defaultSubcat of (defaultCat.subcategories || [])) {
            const defaultSubcatName = (defaultSubcat as SubCategory).name.toLowerCase();
            const existingSubcats = (matchingCat.subcategories || []).map(s => (s as SubCategory).name.toLowerCase());
            
            if (!existingSubcats.includes(defaultSubcatName)) {
              missingSubcatsCheck.push({
                category: defaultCat.name,
                subcategory: (defaultSubcat as SubCategory).name
              });
            }
          }
        }
      }
      console.log('Missing subcategories:', missingSubcatsCheck);

      // Create a map of existing subcategories by name and parent category
      const existingSubcategoriesMap = new Map<string, Map<string, SubCategory>>();
      
      // Populate the subcategories map with parent category name -> subcategory name -> subcategory object
      categories.forEach((category: MainCategory) => {
        const subcategoryMap = new Map<string, SubCategory>();
        
        (category.subcategories || []).forEach((subcategory) => {
          const subcat = subcategory as SubCategory;
          subcategoryMap.set(subcat.name.toLowerCase(), subcat);
        });
        
        existingSubcategoriesMap.set(category.name.toLowerCase(), subcategoryMap);
      });

      // Track new items added
      let addedCategories = 0;
      let addedSubcategories = 0;
      let missingCategories: string[] = [];
      let missingSubcategories: {category: string, subcategory: string}[] = [];

      // Process default categories
      for (const defaultCategory of defaultCategories) {
        const defaultCategoryName = defaultCategory.name.toLowerCase();
        const existingCategory = existingCategoryNamesMap.get(defaultCategoryName);

        if (!existingCategory) {
          // This default category doesn't exist, add it with a new ID
          missingCategories.push(defaultCategory.name);
          const newCategoryId = uuidv4();
          const newCategory = {
            ...defaultCategory,
            id: newCategoryId,
            subcategories: [] // Start with empty subcategories, will add them individually
          };
          
          // Add the category using individual API call
          await addCategory(newCategory);
          addedCategories++;
          
          // Add each subcategory individually
          for (const subcategory of (defaultCategory.subcategories || [])) {
            const subcat = subcategory as SubCategory;
            missingSubcategories.push({category: defaultCategory.name, subcategory: subcat.name});
            
            const newSubcategory = {
              ...subcat,
              id: uuidv4(),
              parentId: newCategoryId,
              // Add snake_case version for database
              parent_id: newCategoryId
            };
            
            // Add the subcategory
            await addSubcategory({
              parentId: newCategoryId,
              subcategory: newSubcategory
            });
            addedSubcategories++;
          }
        } else {
          // Category exists, check for missing subcategories
          const existingSubcategories = existingSubcategoriesMap.get(defaultCategoryName) || new Map<string, SubCategory>();
          
          // Find and add missing subcategories
          for (const defaultSubcategory of (defaultCategory.subcategories || [])) {
            const subcat = defaultSubcategory as SubCategory;
            const subcategoryName = subcat.name.toLowerCase();
            
            if (!existingSubcategories.has(subcategoryName)) {
              missingSubcategories.push({category: defaultCategory.name, subcategory: subcat.name});
              console.log(`Adding missing subcategory: ${subcat.name} under ${defaultCategory.name}`);
              
              // This subcategory doesn't exist, add it individually
              const newSubcategory = {
                ...subcat,
                id: uuidv4(),
                parentId: existingCategory.id,
                // Add snake_case version for database
                parent_id: existingCategory.id,
                color: existingCategory.color,
                type: existingCategory.type
              };
              
              // Add the subcategory
              await addSubcategory({
                parentId: existingCategory.id,
                subcategory: newSubcategory
              });
              addedSubcategories++;
            }
          }
        }
      }

      // Log what was added
      if (missingCategories.length > 0) {
        console.log('Added missing categories:', missingCategories);
      }
      if (missingSubcategories.length > 0) {
        console.log('Added missing subcategories:', missingSubcategories);
      }

      // Refetch categories to update the UI
      await refetch();
      
      if (addedCategories > 0 || addedSubcategories > 0) {
        toast.success(`Added ${addedCategories} categories and ${addedSubcategories} subcategories`);
      } else {
        toast.info('All default categories are already present');
      }
      
      setShowResetConfirmation(false);
    } catch (error) {
      console.error('Error adding missing categories:', error);
      toast.error('Failed to add missing categories');
    }
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-background-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div className="h-full flex flex-col px-2 sm:px-6 py-3 sm:py-4 space-y-4">
      <motion.header className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl shadow-sm sticky top-3 z-10">
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-6">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="p-2 bg-primary/10 rounded-xl">
                <Settings2 className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h1 className="text-lg sm:text-2xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                  {t('settings.categoriesSettings.title')}
                </h1>
                <p className="text-sm text-text-secondary line-clamp-2 sm:line-clamp-none">
                  {t('settings.categoriesSettings.description')}
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                setModalMode('add');
                setSelectedCategory(null);
                setShowMainCategoryModal(true);
              }}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 transition-all duration-200 shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 rounded-xl h-11"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('settings.categoriesSettings.addCategory')}
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary w-4 h-4" />
              <Input
                placeholder={t('settings.categoriesSettings.search')}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-9 w-full bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark rounded-xl h-11"
              />
              {filter && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-primary/5 dark:hover:bg-primary/10"
                  onClick={() => setFilter('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Select 
                value={typeFilter} 
                onValueChange={(value) => setTypeFilter(value as 'all' | 'income' | 'expense')}
              >
                <SelectTrigger className="w-full sm:w-[180px] bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark rounded-xl h-11">
                  <SelectValue placeholder={t('settings.categoriesSettings.filterByType')} />
                </SelectTrigger>
                <SelectContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150">
                  <SelectItem value="all">{t('settings.categoriesSettings.allCategories')}</SelectItem>
                  <SelectItem value="income">{t('settings.categoriesSettings.income')}</SelectItem>
                  <SelectItem value="expense">{t('settings.categoriesSettings.expense')}</SelectItem>
                </SelectContent>
              </Select>

              <AlertDialog open={showResetConfirmation} onOpenChange={setShowResetConfirmation}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-11 px-3 sm:px-4 flex items-center gap-2 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark hover:bg-primary/5 dark:hover:bg-primary/10 rounded-xl"
                    title="Add Missing Default Categories"
                  >
                    <Settings2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Add Missing Categories</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark rounded-xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-lg font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                      Add Missing Default Categories
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-text-secondary">
                      This will add any missing default categories and subcategories without affecting your existing ones. Your current categories and their data will remain unchanged.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark hover:bg-muted/50 dark:hover:bg-muted-dark/50 rounded-xl">
                      {t('common.cancel')}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleResetToDefaultCategories}
                      className="bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 transition-all duration-200 shadow-sm hover:shadow-md dark:shadow-primary-dark/10 dark:hover:shadow-primary-dark/20 rounded-xl"
                    >
                      {t('common.confirm')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => refetch()}
                className="h-11 w-11"
                title="Refresh transaction counts"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="flex-1 overflow-y-auto">
        <motion.div 
          className="space-y-3 sm:space-y-4 max-w-[1920px] mx-auto p-2"
          variants={container}
          initial="hidden"
          animate="show"
        >
          {filteredCategories.length === 0 ? (
            <motion.div 
              className="flex flex-col items-center justify-center py-6 sm:py-16 text-center px-3 sm:px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="rounded-full bg-primary/10 p-3 mb-4">
                <AlertCircle className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-base sm:text-lg font-medium text-text-primary dark:text-text-dark-primary">
                {filter
                  ? "No categories match your search"
                  : "No categories found"}
              </h3>
              <p className="text-sm text-text-secondary mt-1 max-w-md mx-auto">
                {filter
                  ? "Try adjusting your search terms or clear the search to see all categories."
                  : "Start by adding your first category to organize your transactions."}
              </p>
              {!filter && (
                <Button
                  onClick={() => {
                    setModalMode('add');
                    setSelectedCategory(null);
                    setShowMainCategoryModal(true);
                  }}
                  className="mt-6 bg-primary hover:bg-primary/90 text-white dark:bg-primary-dark dark:hover:bg-primary-dark/90 transition-all duration-200 shadow-sm hover:shadow-md dark:shadow-primary-dark/10 dark:hover:shadow-primary-dark/20 rounded-xl h-11"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Category
                </Button>
              )}
            </motion.div>
          ) : (
            filteredCategories.map((category: MainCategory) => (
              <motion.div
                key={category.id}
                variants={item}
                className="group space-y-3"
              >
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between p-3 sm:p-4 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 hover:shadow-md hover:border-primary/30 dark:hover:border-primary-dark/30 transition-all duration-200">
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="text-text-secondary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors p-1 -m-1 rounded-lg touch-manipulation"
                    >
                      {expandedCategories[category.id] ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: category.color }}
                    />
                    <CategoryIcon 
                      iconName={category.icon} 
                      className="w-5 h-5" 
                      style={{ color: category.color }} 
                    />
                                          <div className="flex flex-col flex-1">
                        <span className="font-medium text-text-primary dark:text-text-dark-primary">{category.name}</span>
                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                          <span className="capitalize">{category.type}</span>
                          <span>•</span>
                          <span>{(category.subcategories as SubCategory[]).reduce((total, sub) => total + (sub.transactionCount || 0), 0)} transaction{((category.subcategories as SubCategory[]).reduce((total, sub) => total + (sub.transactionCount || 0), 0)) !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end border-t sm:border-t-0 pt-3 sm:pt-0 mt-3 sm:mt-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleAddSubcategoryClick(category)}
                      className="h-9 w-9 text-primary hover:text-primary hover:bg-primary/10 rounded-lg touch-manipulation"
                    >
                      <FolderPlus className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditMainCategory(category)}
                      className="h-9 w-9 text-primary hover:text-primary hover:bg-primary/10 rounded-lg touch-manipulation"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteMainCategory(category.id)}
                      className="h-9 w-9 text-destructive hover:text-destructive hover:bg-error/10 rounded-lg touch-manipulation"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {expandedCategories[category.id] && (
                  <motion.div 
                    className="pl-6 sm:pl-8 space-y-3"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {((category.subcategories || []) as SubCategory[])
                      .sort((a: SubCategory, b: SubCategory) => a.name.localeCompare(b.name))
                      .map((subcategory: SubCategory) => (
                      <motion.div
                        key={subcategory.id}
                        variants={item}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className="group flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 rounded-xl border border-border dark:border-border-dark bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 hover:shadow-md hover:border-primary/30 dark:hover:border-primary-dark/30 transition-all duration-200"
                      >
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                          <CategoryIcon 
                            iconName={subcategory.icon} 
                            className="w-4 h-4" 
                            style={{ color: category.color }} 
                          />
                          <div className="flex flex-col flex-1">
                            <span className="text-text-primary dark:text-text-dark-primary">{subcategory.name}</span>
                            <span className="text-text-secondary">
                              {subcategory.transactionCount || 0} transaction{(subcategory.transactionCount || 0) !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-end border-t sm:border-t-0 pt-3 sm:pt-0 mt-3 sm:mt-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditSubCategory(category, subcategory)}
                            className="h-9 w-9 text-primary hover:text-primary hover:bg-primary/10 rounded-lg touch-manipulation"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteSubcategory(category.id, subcategory.id)}
                            className="h-9 w-9 text-destructive hover:text-destructive hover:bg-error/10 rounded-lg touch-manipulation"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                    {(category.subcategories || []).length === 0 && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        className="flex flex-col items-center justify-center py-6 text-center"
                      >
                        <div className="rounded-full bg-primary/10 p-2 mb-3">
                          <FolderPlus className="w-5 h-5 text-primary" />
                        </div>
                        <p className="text-sm text-text-secondary">
                          No subcategories yet
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedCategory(category);
                            setShowSubCategoryModal(true);
                          }}
                          className="mt-2 text-primary hover:text-primary hover:bg-primary/10 rounded-lg touch-manipulation"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Subcategory
                        </Button>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </motion.div>
            ))
          )}
        </motion.div>
      </div>

      {/* Keep existing modals */}
      {showMainCategoryModal && (
        <CategoryModal
          type="main"
          mode={modalMode}
          editingCategory={modalMode === 'edit' && selectedCategory ? selectedCategory as MainCategory : undefined}
          onClose={() => {
            setShowMainCategoryModal(false);
            setSelectedCategory(null);
          }}
          onSave={(category) => {
            if (modalMode === 'edit') {
              handleUpdateMainCategory(category.id, category);
            } else {
              handleAddMainCategory(category);
            }
          }}
        />
      )}

      {showSubCategoryModal && selectedCategory && (
        <CategoryModal
          type="sub"
          mode={modalMode}
          parentCategory={selectedCategory}
          editingCategory={modalMode === 'edit' && selectedSubCategory ? selectedSubCategory : undefined}
          onClose={() => {
            setShowSubCategoryModal(false);
            setSelectedCategory(null);
            setSelectedSubCategory(null);
          }}
          onSave={(subcategory) => {
            if (modalMode === 'edit' && selectedCategory && selectedSubCategory) {
              handleUpdateSubcategory(selectedCategory.id, selectedSubCategory.id, subcategory);
            } else if (selectedCategory) {
              handleAddSubCategory(subcategory);
            }
            setShowSubCategoryModal(false);
          }}
        />
      )}
    </motion.div>
  );
}